"use client";
import {
  makeArray,
  ShowroomLocation,
  VehicleBodyType,
  VehicleFeature,
  VehicleMake,
  VehicleModel,
  VehicleModelGetDTO,
  VehicleSort,
} from "@alba-cars/common-modules";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";

import { fuelTypeOptions } from "../_components/_constants";
import { useFilterContext } from "../_components/context/FilterContext";
import { ShowroomLocationOptions } from "../_components/types";
import { getAllColors } from "@/lib/api/colors/get-all-colors";
import { getVehicleBodyTypesOptions } from "@/lib/api/get-body-types";
import { getVehicleMakesOptions } from "@/lib/api/get-makes";
import { getVehicleModelsOptions } from "@/lib/api/get-models";
import { getVehicleFeaturesOptions } from "@/lib/api/get-vehicle-features";
import {
  getLocationPresetOptions,
  LocationPresetOption,
} from "@/lib/api/location-presets/get-location-presets";
import { getShowroomLocationOptions } from "@/lib/api/showroom-locations/get-showroom-locations";

const CACHE_TIME = 30 * 60 * 1000; // 30 minutes
const STALE_TIME = 10 * 60 * 1000; // 10 minutes

const createQueries = (makes: string | string[] | undefined, options?: { locale?: "en" | "ar" | "ru" | "zh" }) => [
  {
    queryKey: ["car-makes"],
    // * Pass "relevant" to only get the makes that have cars in stock
    queryFn: () =>
      getVehicleMakesOptions({ relevant: "1", options: { limit: 1000, validate: () => [] }, validate: () => [] }),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["car-models", { makes: makeArray(makes) }],
    queryFn: () => {
      const dto: Omit<VehicleModelGetDTO, "validate"> & { relevant?: string } = {
        filter: {
          makes,
          validate(): string[] {
            return []; // Implement proper validation if needed
          },
        },
        options: {
          limit: 1000,
          validate: () => [],
        },
        // * This is to get only the models that have cars in stock
        relevant: "1",
      };

      return getVehicleModelsOptions(dto);
    },
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    enabled: !!makes,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["car-features", options?.locale],
    queryFn: () =>
      getVehicleFeaturesOptions(
        {
          options: { limit: 1000, validate: () => [] },
          validate: () => [],
        },
        { locale: options?.locale as "en" | "ar" | "ru" | "zh" },
      ),
    staleTime: Infinity, // Features rarely change
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["car-body-types", options?.locale],
    // * Pass "relevant" to only get the body types that have cars in stock
    queryFn: () =>
      getVehicleBodyTypesOptions(
        {
          relevant: "1",
          options: { limit: 1000, validate: () => [] },
          validate: () => [],
        },
        { locale: options?.locale as "en" | "ar" | "ru" | "zh" },
      ),
    staleTime: Infinity, // Body types rarely change
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["colors", options?.locale],
    queryFn: async () => {
      const colors = await getAllColors(options?.locale as "en" | "ar" | "ru" | "zh", true);

      return colors.map((color) => {
        let localizedName = color.name;

        if (options?.locale === "ar") {
          localizedName = color.nameArabic || color.name;
        } else if (options?.locale === "ru") {
          localizedName = color.nameRussian || color.name;
        } else if (options?.locale === "zh") {
          localizedName = color.nameChinese || color.name;
        }

        return {
          value: color.name,
          label: localizedName,
          hexCode: color.hexCode,
        };
      });
    },
    staleTime: Infinity,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["showroom-locations", options?.locale],
    queryFn: () => getShowroomLocationOptions(options?.locale as "en" | "ar" | "ru" | "zh"),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
  {
    queryKey: ["location-presets"],
    queryFn: () => getLocationPresetOptions(),
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  },
];

export const useVehicleFiltersData = () => {
  const t = useTranslations("Filters");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { queryOptions } = useFilterContext();

  // Initialize make from filter, but don't watch for changes
  const [makes, setMakes] = useState<string | string[] | undefined>(() => queryOptions?.filter?.make ?? undefined);

  const queriesConfig = useMemo(
    () => createQueries(makes, { locale: locale as "en" | "ar" | "ru" | "zh" }),
    [makes, locale],
  );

  const queries = useQueries({
    queries: queriesConfig,
  });

  const [
    { data: makesOptions = [], isLoading: isMakesOptionsLoading },
    { data: modelsOptions = [], isLoading: isModelsOptionsLoading },
    { data: featuresOptions = [], isLoading: isFeaturesOptionsLoading },
    { data: bodyTypeOptions = [], isLoading: isBodyTypeOptionsLoading },
    { data: colorsOptions = [], isLoading: isColorsOptionsLoading },
    { data: showroomLocationOptions = [], isLoading: isShowroomLocationsLoading },
    { data: locationPresetOptions = [], isLoading: isLocationPresetsLoading },
  ] = queries;

  const sortOptions = useMemo(
    (): { label: string; value: VehicleSort }[] => [
      { label: t("sortOptions.yearNewest"), value: { year: "desc" } },
      { label: t("sortOptions.newest"), value: { createdAt: "desc" } },
      { label: t("sortOptions.priceHighToLow"), value: { finance: { price: "desc" } } },
      { label: t("sortOptions.priceLowToHigh"), value: { finance: { price: "asc" } } },
      { label: t("sortOptions.mileageLow"), value: { mileage: "asc" } },
    ],
    [t],
  );

  const makesLookup = useMemo(
    () =>
      new Map(
        (makesOptions as unknown as (VehicleMake & { label: string; value: string })[])?.map((make) => [
          make.slug,
          make,
        ]) ?? [],
      ),
    [makesOptions],
  );

  const modelsLookup = useMemo(
    () =>
      new Map(
        (modelsOptions as unknown as (VehicleModel & { label: string; value: string })[])?.map((model) => [
          model.slug,
          model,
        ]) ?? [],
      ),
    [modelsOptions],
  );

  const bodyTypeLookup = useMemo(
    () =>
      new Map(
        (bodyTypeOptions as unknown as (VehicleBodyType & { label: string; value: string; slug: string })[])?.map(
          (bodyType) => [bodyType.slug, bodyType],
        ) ?? [],
      ),
    [bodyTypeOptions],
  );

  const fuelTypeLookup = useMemo(() => new Map(fuelTypeOptions.map((fuelType) => [fuelType.value, fuelType])), []);

  const colorsLookup = useMemo(
    () =>
      new Map(
        (colorsOptions as unknown as { label: string; value: string; hexCode: string }[])?.map((color) => [
          color.value,
          color,
        ]),
      ),
    [colorsOptions],
  );

  const featuresLookup = useMemo(
    () =>
      new Map(
        (featuresOptions as unknown as (VehicleFeature & { label: string; value: string })[])?.map((option) => [
          option.value,
          option,
        ]) ?? [],
      ),
    [featuresOptions],
  );

  const showroomLocationLookup = useMemo(
    () =>
      new Map(
        (
          showroomLocationOptions as unknown as (ShowroomLocation & { label: string; value: string })[]
        )?.map((showroom) => [showroom.slug, showroom]) ?? [],
      ),
    [showroomLocationOptions],
  );

  const locationPresetLookup = useMemo(
    () =>
      new Map(
        (locationPresetOptions as LocationPresetOption[])?.map((preset) => [preset.slug, preset]) ?? [],
      ),
    [locationPresetOptions],
  );

  const handleSetMakes = useCallback(
    (newMakes: string | string[] | undefined) => {
      setMakes(newMakes);
      // Clear models cache when make changes
      if (!newMakes) {
        queryClient.removeQueries({ queryKey: ["car-models"] });
      }
    },
    [queryClient],
  );

  return {
    sortOptions,
    makesOptions,
    modelsOptions,
    featuresOptions,
    bodyTypeOptions,
    colorsOptions,
    colorsLookup,
    showroomLocationOptions: (showroomLocationOptions ?? []) as ShowroomLocationOptions,
    showroomLocationLookup,
    locationPresetOptions: (locationPresetOptions ?? []) as LocationPresetOption[],
    locationPresetLookup,
    isMakesOptionsLoading,
    isModelsOptionsLoading,
    isFeaturesOptionsLoading,
    isBodyTypeOptionsLoading,
    isColorsOptionsLoading,
    isShowroomLocationsLoading,
    isLocationPresetsLoading,
    makesLookup,
    modelsLookup,
    bodyTypeLookup,
    featuresLookup,
    fuelTypeOptions,
    fuelTypeLookup,
    setMakes: handleSetMakes,
  } as const;
};
